// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: Teams group-chat bridge state. `links` backs the Teams badge next to
// DMs and groups (one request for all channels); `chats` is the picker in
// Connections → Microsoft.

import {Endpoints} from '@app/features/app/constants/Endpoints';
import {http} from '@app/features/platform/transport/RestTransport';
import {makeAutoObservable, runInAction} from 'mobx';

export interface TeamsChannelLink {
	kind: 'group' | 'dm';
	chat_id: string;
	channel_id: string;
	name: string | null;
	member_count: number;
	linked: boolean;
	mirror: boolean;
	owner: boolean;
}

export interface TeamsGroupChat {
	chat_id: string;
	name: string;
	member_count: number;
	members: Array<string>;
	last_activity: string | null;
	linked: boolean;
	owner: boolean;
	mirror: boolean;
	channel_id: string | null;
}

interface LinkResponse {
	chat_id: string;
	channel_id: string;
	name: string;
	member_count: number;
	linked: boolean;
	mirror: boolean;
	owner: boolean;
	subscribed?: boolean;
}

const LINKS_TTL_MS = 5 * 60 * 1000;

class KonvoTeamsGroupsStore {
	links: ReadonlyMap<string, TeamsChannelLink> = new Map();
	chats: Array<TeamsGroupChat> | null = null;
	chatsError: 'unreachable' | 'failed' | null = null;
	loadingChats = false;
	busyChatId: string | null = null;
	private linksLoadedAt = 0;
	private linksPending = false;

	constructor() {
		makeAutoObservable<this, 'linksLoadedAt' | 'linksPending'>(
			this,
			{linksLoadedAt: false, linksPending: false},
			{autoBind: true},
		);
	}

	linkFor(channelId: string): TeamsChannelLink | null {
		return this.links.get(channelId) ?? null;
	}

	/** Fetch the badge map once (and again after it goes stale); safe from render paths. */
	ensureLinks(): void {
		if (this.linksPending) return;
		if (Date.now() - this.linksLoadedAt < LINKS_TTL_MS) return;
		this.linksPending = true;
		void http
			.get<{links?: Array<TeamsChannelLink>}>(Endpoints.KONVO_TEAMS_LINKS)
			.then((response) => {
				runInAction(() => {
					this.links = new Map((response.body?.links ?? []).map((link) => [link.channel_id, link]));
					this.linksLoadedAt = Date.now();
				});
			})
			.catch(() => {
				runInAction(() => {
					// Do not hammer the API on a failure; try again in a minute.
					this.linksLoadedAt = Date.now() - LINKS_TTL_MS + 60_000;
				});
			})
			.finally(() => {
				this.linksPending = false;
			});
	}

	invalidate(): void {
		this.linksLoadedAt = 0;
		this.ensureLinks();
	}

	async loadChats(): Promise<void> {
		if (this.loadingChats) return;
		this.loadingChats = true;
		this.chatsError = null;
		try {
			const response = await http.get<{chats?: Array<TeamsGroupChat>}>(Endpoints.KONVO_TEAMS_GROUPS);
			runInAction(() => {
				this.chats = response.body?.chats ?? [];
			});
		} catch (error) {
			runInAction(() => {
				const status = (error as {status?: number} | null)?.status;
				this.chatsError = status === 502 ? 'unreachable' : 'failed';
			});
		} finally {
			runInAction(() => {
				this.loadingChats = false;
			});
		}
	}

	private applyLink(chatId: string, patch: Partial<TeamsGroupChat>): void {
		if (!this.chats) return;
		this.chats = this.chats.map((chat) => (chat.chat_id === chatId ? {...chat, ...patch} : chat));
	}

	async link(chatId: string): Promise<LinkResponse> {
		this.busyChatId = chatId;
		try {
			const response = await http.post<LinkResponse>(Endpoints.KONVO_TEAMS_GROUP_LINK(chatId), {body: {}});
			const body = response.body;
			runInAction(() => {
				if (body) {
					this.applyLink(chatId, {
						linked: body.linked,
						owner: body.owner,
						mirror: body.mirror,
						channel_id: body.channel_id,
						name: body.name,
					});
				}
			});
			this.invalidate();
			return body as LinkResponse;
		} finally {
			runInAction(() => {
				this.busyChatId = null;
			});
		}
	}

	async unlink(chatId: string): Promise<void> {
		this.busyChatId = chatId;
		try {
			await http.delete(Endpoints.KONVO_TEAMS_GROUP_LINK(chatId));
			runInAction(() => {
				this.applyLink(chatId, {linked: false, owner: false, mirror: false, channel_id: null});
			});
			this.invalidate();
		} finally {
			runInAction(() => {
				this.busyChatId = null;
			});
		}
	}

	async setMirror(chatId: string, enabled: boolean): Promise<void> {
		const response = await http.put<LinkResponse>(Endpoints.KONVO_TEAMS_GROUP_MIRROR(chatId), {body: {enabled}});
		runInAction(() => {
			this.applyLink(chatId, {mirror: response.body?.mirror ?? enabled});
		});
		this.invalidate();
	}
}

export default new KonvoTeamsGroupsStore();
